/**
 * ============================================================================
 * dataLayer.ts — Wrapper tipado para el dataLayer de Google Tag Manager
 * ============================================================================
 *
 * Punto único donde se empujan eventos al `window.dataLayer`. Todos los tags
 * (GA4, Google Ads, Meta Pixel, futuros server-side) se configuran DENTRO de
 * GTM (contenedor GTM-K4WC3TVM) y reaccionan a estos eventos.
 *
 * Ventajas de este patrón:
 *   1. El código React nunca sabe a qué plataforma va cada evento
 *      (desacoplamiento total: si mañana quitas Meta, solo cambias GTM).
 *   2. Consent Mode v2 vive en GTM: los tags respetan el consent del usuario.
 *   3. Tipado estricto → autocompletado + prevención de typos.
 *   4. Testeable: en unit tests basta con espiar `window.dataLayer.push`.
 *
 * Eventos GA4 e-commerce estándar:
 *   https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 * ============================================================================
 */

// -----------------------------------------------------------------------------
// Tipos base
// -----------------------------------------------------------------------------

export type BrickshareAccessChannel =
  | "web_desktop"
  | "web_mobile_browser"
  | "web_pwa"
  | "app_android"
  | "app_ios";

export type BrickshareAppPlatform = "web" | "android" | "ios";

export interface NativeAppRuntimeConfig {
  platform: "android" | "ios";
  appId: string;
  appVersion: string;
  isNativeApp: true;
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    __BRICKSHARE_NATIVE_APP__?: NativeAppRuntimeConfig;
    __BRICKSHARE_NATIVE_ANALYTICS_LISTENER__?: (payload: Record<string, unknown>) => void;
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => string;
    };
  }
}

export const BRICKSHARE_APP_ID = "org.brickshare.app";
export const BRICKSHARE_APP_VERSION = "1.0.0";

/**
 * Detecta la plataforma de ejecución actual ("web" | "android" | "ios").
 */
export const getAppPlatform = (): BrickshareAppPlatform => {
  if (typeof window === "undefined") return "web";
  if (window.__BRICKSHARE_NATIVE_APP__?.platform) {
    return window.__BRICKSHARE_NATIVE_APP__.platform;
  }
  if (window.Capacitor?.getPlatform) {
    const p = window.Capacitor.getPlatform();
    if (p === "android") return "android";
    if (p === "ios") return "ios";
  }
  return "web";
};

/**
 * Determina el canal exacto de acceso (`bs_access_channel`) para segmentar en GA4:
 *   - web_desktop
 *   - web_mobile_browser
 *   - web_pwa
 *   - app_android
 *   - app_ios
 */
export const getAccessChannel = (): BrickshareAccessChannel => {
  if (typeof window === "undefined") return "web_desktop";

  const platform = getAppPlatform();
  if (platform === "android") return "app_android";
  if (platform === "ios") return "app_ios";

  try {
    const forced = window.sessionStorage?.getItem("bs_forced_channel") as BrickshareAccessChannel | null;
    if (
      forced === "app_android" ||
      forced === "app_ios" ||
      forced === "web_desktop" ||
      forced === "web_mobile_browser" ||
      forced === "web_pwa"
    ) {
      return forced;
    }
  } catch {
    // no-op
  }

  const isStandalone =
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  if (isStandalone) return "web_pwa";

  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
  const isSmallScreen = typeof window.innerWidth === "number" && window.innerWidth < 768;

  if (isMobileUa || isSmallScreen) return "web_mobile_browser";
  return "web_desktop";
};

export const getChannelTelemetryContext = () => {
  const channel = getAccessChannel();
  const platform = getAppPlatform();
  const isNative = channel === "app_android" || channel === "app_ios";
  return {
    bs_access_channel: channel,
    bs_app_platform: platform,
    bs_app_id: isNative
      ? window.__BRICKSHARE_NATIVE_APP__?.appId || BRICKSHARE_APP_ID
      : "www.brickshare.org",
    bs_app_version: window.__BRICKSHARE_NATIVE_APP__?.appVersion || BRICKSHARE_APP_VERSION,
  };
};

/**
 * Item de un carrito / listado / detalle en el estándar GA4 e-commerce.
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_item
 */
export interface EcommerceItem {
  /** ID del producto (uuid Supabase). Debe ser estable entre eventos. */
  item_id: string;
  /** Nombre visible ("LEGO Star Wars — Millennium Falcon 75192"). */
  item_name: string;
  /** Precio unitario (€). Para préstamos, usar el coste en brickpoints equivalente. */
  price?: number;
  /** Cantidad. Casi siempre 1 en Brickshare. */
  quantity?: number;
  /** Categoría LEGO (Star Wars, City…) — enviado como custom dim theme_lego. */
  item_category?: string;
  /** Subcategoría (age range: "5-7", "8-11", "12+"). */
  item_category2?: string;
  /** Dificultad / nº de piezas (custom dim set_difficulty). */
  item_category3?: string;
  /** Índice dentro de una lista (para view_item_list). */
  index?: number;
  /** Nombre del listado ("catalogo", "featured", "wishlist"). */
  item_list_name?: string;
  /** Marca — siempre "LEGO" en nuestro caso (necesario para reports GA4). */
  item_brand?: string;
  /** Variante (color, edición limitada…). Opcional. */
  item_variant?: string;
  /** Set ID original de LEGO (75192, 42115…) útil para deep-linking. */
  affiliation?: string;
}

/**
 * Payload estándar de un evento e-commerce GA4.
 */
export interface EcommercePayload {
  /** Moneda ISO 4217. */
  currency?: string;
  /** Valor monetario del evento (suma de items × qty). */
  value?: number;
  /** ID de transacción (Stripe checkout_session_id o similar). */
  transaction_id?: string;
  /** Cupón aplicado (código). */
  coupon?: string;
  /** Método de envío. */
  shipping_tier?: string;
  /** Método de pago. */
  payment_type?: string;
  /** Nombre del listado (útil en view_item_list / select_item). */
  item_list_name?: string;
  /** ID del listado. */
  item_list_id?: string;
  /** Items involucrados. Opcional: GA4 solo lo exige en `purchase`. */
  items?: EcommerceItem[];
}

// -----------------------------------------------------------------------------
// Catálogo de eventos GA4 estándar soportados
// -----------------------------------------------------------------------------

/**
 * Nombres de eventos e-commerce estándar de GA4 que usamos en Brickshare.
 * El nombre EXACTO importa: GA4 los detecta y los muestra en reports nativos.
 */
export type GA4EcommerceEvent =
  | "view_item_list"
  | "select_item"
  | "view_item"
  | "add_to_wishlist"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout"
  | "add_payment_info"
  | "add_shipping_info"
  | "purchase"
  | "refund";

/**
 * Otros eventos GA4 recomendados (no e-commerce).
 */
export type GA4StandardEvent =
  | "sign_up"
  | "login"
  | "share"
  | "search"
  | "select_content"
  | "generate_lead"
  | "join_group"
  | "tutorial_begin"
  | "tutorial_complete";

/**
 * Eventos custom propios de Brickshare (namespace bs_).
 * Prefijo para distinguirlos de los estándar en informes GA4.
 */
export type BrickshareCustomEvent =
  | "bs_section_view"
  | "bs_section_engagement"
  | "bs_language_change"
  | "bs_filter_applied"
  | "bs_search_no_results"
  | "bs_product_hover"
  | "bs_rage_click"
  | "bs_dead_click"
  | "bs_journey_step"

  | "bs_wishlist_position_change"
  | "bs_referral_click"
  | "bs_donation_click"
  | "bs_beta_signup"
  | "bs_subscribe_click"
  | "bs_swap_requested"
  | "bs_return_requested"
  // Funnel de conversion
  | "bs_plan_selected"
  | "bs_signup_start"
  | "bs_checkout_abandoned"
  | "bs_first_wishlist_add"
  // Modelo P2P (préstamo entre particulares)
  | "bs_borrow_requested"
  | "bs_borrow_completed"
  | "bs_borrow_cancelled"
  | "rental_request_complete"
  // Oferta y catálogo
  | "bs_set_added"
  | "bs_set_modal_view"
  // Errores de pago (abandono de funnel de checkout, por paso/dispositivo)
  | "bs_payment_error"
  // Onboarding / dashboard / membresía
  | "bs_profile_completed"
  | "bs_dashboard_tab_changed"
  | "bs_pricing_cta_clicked"
  | "bs_card_setup_complete"
  | "bs_promo_code_applied"
  // Mobile Sticky CTA
  | "bs_sticky_cta_view"
  | "bs_sticky_cta_click";

export type EventName =
  | GA4EcommerceEvent
  | GA4StandardEvent
  | BrickshareCustomEvent
  | "page_view"
  | "language_change";

// -----------------------------------------------------------------------------
// Helper principal
// -----------------------------------------------------------------------------

const isBrowser = (): boolean => typeof window !== "undefined";

/**
 * Empuja un objeto arbitrario al dataLayer.
 * Guarda de seguridad: si dataLayer no está inicializado lo crea.
 * En dev, loguea el evento por consola.
 *
 * NO usar directamente: preferir los helpers tipados de más abajo.
 */
export const pushDataLayer = (payload: Record<string, unknown>): void => {
  if (!isBrowser()) return;
  try {
    const enrichedPayload =
      typeof payload.event === "string"
        ? {
            ...getChannelTelemetryContext(),
            ...payload,
          }
        : payload;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(enrichedPayload);

    if (typeof window.__BRICKSHARE_NATIVE_ANALYTICS_LISTENER__ === "function" && typeof enrichedPayload.event === "string") {
      try {
        window.__BRICKSHARE_NATIVE_ANALYTICS_LISTENER__(enrichedPayload);
      } catch {
        // Ignorar errores del listener nativo secundario
      }
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[dataLayer] push", enrichedPayload);
    }
  } catch (err) {
    // Nunca romper la app por un fallo de tracking
    // eslint-disable-next-line no-console
    console.warn("[dataLayer] push failed", err);
  }
};

/**
 * Empuja un evento genérico (custom o estándar no-ecommerce).
 * Añade automáticamente el timestamp y el path actual.
 */
export const trackEvent = (
  event: EventName,
  params: Record<string, unknown> = {}
): void => {
  pushDataLayer({
    event,
    page_path: isBrowser() ? window.location.pathname : undefined,
    timestamp: Date.now(),
    ...params,
  });
};

/**
 * Envia una conversion directa de Google Ads (gtag + dataLayer).
 * ID de conversion de Google Ads: AW-18457539701
 * Labels configuradas:
 *   - 'wishlist': 'AW-18457539701/6ntfCK6fkIUdEPXonuFE' (Registro en la Wishlist)
 *   - 'purchase': 'AW-18457539701/PpquCOir-PocEPXonuFE' (Compra)
 */
export const GOOGLE_ADS_ID = "AW-18457539701";
export const GOOGLE_ADS_LABELS = {
  wishlist: "AW-18457539701/6ntfCK6fkIUdEPXonuFE",
  purchase: "AW-18457539701/PpquCOir-PocEPXonuFE",
} as const;

export const trackGoogleAdsConversion = (
  type: keyof typeof GOOGLE_ADS_LABELS,
  params: {
    value?: number;
    currency?: string;
    transaction_id?: string;
    callback?: () => void;
  } = {}
): void => {
  if (!isBrowser()) return;
  const sendTo = GOOGLE_ADS_LABELS[type];
  if (!sendTo) return;

  // 1. DataLayer event para tags GTM
  pushDataLayer({
    event: "google_ads_conversion",
    conversion_type: type,
    google_ads_send_to: sendTo,
    value: params.value ?? 1.0,
    currency: params.currency ?? "EUR",
    transaction_id: params.transaction_id || undefined,
  });

  // 2. Direct gtag call si está definido en window (Consent Mode v2)
  if (typeof window.gtag === "function") {
    try {
      window.gtag("event", "conversion", {
        send_to: sendTo,
        value: params.value ?? 1.0,
        currency: params.currency ?? "EUR",
        transaction_id: params.transaction_id || undefined,
        event_callback: params.callback,
      });
    } catch (e) {
      // no-op si falla gtag directo
    }
  }
};

/**
 * Empuja un evento e-commerce con el formato ESTÁNDAR GA4.
 * GA4 espera los items dentro de `ecommerce.items` (no en la raíz).
 *
 * IMPORTANTE: siempre limpia el ecommerce anterior antes de pushear el nuevo,
 * para evitar contaminación entre eventos (recomendación oficial de Google).
 *
 * @example
 *   trackEcommerce("view_item", {
 *     currency: "EUR",
 *     value: 29.90,
 *     items: [{ item_id: "uuid", item_name: "Millennium Falcon", price: 29.90 }],
 *   });
 */
export const trackEcommerce = (
  event: GA4EcommerceEvent,
  payload: EcommercePayload
): void => {
  // 1. Reset del objeto ecommerce anterior (evita reportes cruzados)
  pushDataLayer({ ecommerce: null });

  // 2. Push del nuevo evento
  pushDataLayer({
    event,
    ecommerce: {
      currency: payload.currency ?? "EUR",
      ...payload,
    },
    page_path: isBrowser() ? window.location.pathname : undefined,
    timestamp: Date.now(),
  });

  // 3. Si es un purchase, activar también la conversión de Google Ads
  if (event === "purchase") {
    trackGoogleAdsConversion("purchase", {
      value: payload.value,
      currency: payload.currency ?? "EUR",
      transaction_id: payload.transaction_id,
    });
  }
};

/**
 * Actualiza user properties en GA4 (persistente durante la sesión).
 * Se traduce en GTM a un tag GA4 con "user properties" configuradas.
 *
 * @example
 *   setUserProperties({ language: "es", plan_tier: "p2p" });
 */
export const setUserProperties = (
  props: Record<string, string | number | boolean | null>
): void => {
  pushDataLayer({
    event: "bs_user_properties_update",
    user_properties: props,
  });
};

/**
 * Setea el user_id en GA4 (útil para conectar sesiones cross-device).
 * Debe ser un ID interno estable (uuid de Supabase), NUNCA un email/teléfono
 * en claro para cumplir con la política de datos personales de Google.
 */
export const setUserId = (userId: string | null): void => {
  pushDataLayer({
    event: "bs_user_id_update",
    user_id: userId,
  });
};

/**
 * Empuja un page_view manual (para SPA). GTM tiene un trigger nativo
 * "History Change" que puede capturar esto automáticamente, pero hacerlo
 * explícito nos da control fino sobre el `page_title`.
 */
export const trackPageView = (path: string, title: string): void => {
  pushDataLayer({
    event: "page_view",
    page_path: path,
    page_location: isBrowser() ? window.location.origin + path : "",
    page_title: title,
    timestamp: Date.now(),
  });
};

/**
 * Recupera el client_id de GA4 de forma asíncrona.
 *
 * Necesario para enviar conversiones server-side (Measurement Protocol desde
 * el webhook de Stripe) atribuidas a la MISMA sesión/usuario de GA4. Se guarda
 * en `user_metadata.ga4_client_id` al hacer login (ver AuthContext).
 *
 * Devuelve null si gtag aún no está listo (consent denegado, GTM no cargado
 * todavía) — nunca lanza ni bloquea.
 */
export const GA4_MEASUREMENT_ID = "G-H511LVQLMX";

export const getGa4ClientId = (
  measurementId: string = GA4_MEASUREMENT_ID
): Promise<string | null> => {
  return new Promise((resolve) => {
    if (!isBrowser() || typeof window.gtag !== "function") return resolve(null);
    try {
      let settled = false;
      window.gtag("get", measurementId, "client_id", (id: string) => {
        settled = true;
        resolve(id ?? null);
      });
      // Timeout de seguridad: si gtag no responde en 1s, no bloquear el flujo.
      setTimeout(() => {
        if (!settled) resolve(null);
      }, 1000);
    } catch {
      resolve(null);
    }
  });
};

// -----------------------------------------------------------------------------
// Enhanced Conversions (user-provided data hasheada) — Google Ads / GA4
// -----------------------------------------------------------------------------
//
// Las Enhanced Conversions mejoran el matching de conversiones enviando datos
// de usuario HASHEADOS (SHA-256) junto al evento de conversión. Con Consent
// Mode v2 + la conversión GA4-importada ("brickshare (web) purchase",
// id 7697853083) + el toggle "User-provided data collection" activado en GA4,
// GA4 propaga estos datos a Google Ads a través del link GA4↔Ads.
//
// IMPORTANTE (privacidad / política de Google):
//   - El email se NORMALIZA (trim + lowercase) y se HASHEA con SHA-256 ANTES de
//     salir del navegador. Nunca se envía en claro al dataLayer.
//   - El teléfono debe ir en formato E.164 (+34…) antes de hashear.
//   - Solo se debe enviar tras consent (ad_user_data = granted). El envío se
//     hace en el evento `purchase`, que solo dispara tras completar el pago.
//
// Referencia:
//   https://support.google.com/google-ads/answer/13258081 (Enhanced Conversions)
//   https://developers.google.com/tag-platform/gtagjs/reference#set (user_data)

/** Interfaz de datos de usuario en claro (se hashean antes de enviar). */
export interface UserProvidedData {
  /** Email en claro. Se normaliza y hashea (SHA-256) antes de enviar. */
  email?: string | null;
  /** Teléfono en formato E.164 (+34…). Se hashea (SHA-256). */
  phone?: string | null;
}

/** Hash SHA-256 → hex minúsculas (formato que exige Google para EC). */
const sha256Hex = async (input: string): Promise<string | null> => {
  if (!isBrowser() || !window.crypto?.subtle) return null;
  try {
    const data = new TextEncoder().encode(input);
    const buf = await window.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
};

/** Normaliza un email según las reglas de Google (trim + lowercase). */
const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/**
 * Empuja al dataLayer los datos de usuario HASHEADOS para Enhanced Conversions.
 * GTM debe tener un tag/variable que lea `user_data.sha256_email_address` y
 * `user_data.sha256_phone_number` y los pase al tag de GA4/Ads.
 *
 * Se debe llamar ANTES (o junto) al evento `purchase` para que el matching
 * asocie la conversión al usuario. Nunca lanza ni bloquea el flujo de pago.
 *
 * @example
 *   await setUserProvidedData({ email: user.email });
 *   trackEcommerce("purchase", { transaction_id, value, currency, items });
 */
export const setUserProvidedData = async (
  data: UserProvidedData
): Promise<void> => {
  if (!isBrowser()) return;
  try {
    const userData: Record<string, string> = {};

    if (data.email) {
      const hashed = await sha256Hex(normalizeEmail(data.email));
      if (hashed) userData.sha256_email_address = hashed;
    }
    if (data.phone) {
      // El teléfono debe venir ya en E.164; solo quitamos espacios.
      const hashed = await sha256Hex(data.phone.replace(/\s+/g, ""));
      if (hashed) userData.sha256_phone_number = hashed;
    }

    if (Object.keys(userData).length === 0) return;

    pushDataLayer({
      event: "bs_user_data_ready",
      user_data: userData,
    });
  } catch (err) {
    // Nunca romper el checkout por un fallo de tracking
    // eslint-disable-next-line no-console
    console.warn("[dataLayer] setUserProvidedData failed", err);
  }
};
